import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import invitationService from '../services/invitationService'
import agentService from '../services/agentService'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { useTranslation } from '../hooks/use-i18n'
import { canManageRoom } from '../utils/roomPermissions'

interface InvitePlayerModalProps {
  roomId?: string
  kind?: 'room' | 'world_football' | 'world_formula1'
  onClose: () => void
  onSuccess: () => void
}

interface InviteForm {
  recipientEmail: string
  role?: string
  startingPoints?: string
}

// Corporate role types (matching CreateRoomModal)
const corporateRoles = [
  'Executive',
  'Operations',
  'Finance & Accounting',
  'Human Resources',
  'Sales',
  'Marketing',
  'Product',
  'Research & Development',
  'Development',
  'Customer Service Support',
  'Legal & Compliance',
  'Procurement & Purchasing',
  'Supply Chain / Logistics',
  'Facilities & Administration',
] as const

const InvitePlayerModal: React.FC<InvitePlayerModalProps> = ({ roomId, kind = 'room', onClose, onSuccess }) => {
  const { t, locale } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [draftLoading, setDraftLoading] = useState(false)
  const [inviteDraft, setInviteDraft] = useState<{ subject: string; body: string } | null>(null)
  const [draftMessage, setDraftMessage] = useState('')
  const { singleRoom } = useSelector((state: RootState) => state.rooms)
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const { register, handleSubmit, formState: { errors } } = useForm<InviteForm>({
    defaultValues: {
      role: 'Executive'
    }
  })
  
  const isWorld = kind === 'world_football' || kind === 'world_formula1'
  const isCompanyRoom = !isWorld && singleRoom?.type === 'company'
  const isFootballRoom = (singleRoom?.gameType ?? 'football') === 'football'
  const canAssignPoints = !isWorld && isFootballRoom && canManageRoom(singleRoom, currentUser?._id)
  const title = isWorld
    ? (kind === 'world_formula1' ? t('invitations.inviteWorldFormula1') : t('invitations.inviteWorldFootball'))
    : t('roomPage.invitePlayer')

  const handleDraftInvite = async () => {
    if (!roomId) return
    try {
      setDraftLoading(true)
      const res = await agentService.inviteDraft({
        roomId,
        recipientEmail: undefined,
        message: draftMessage.trim() || undefined,
        locale,
      })
      setInviteDraft(res.proposeInvite)
      toast.success(t('agent.inviteDraftReady'))
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : t('agent.errorGeneric'))
    } finally {
      setDraftLoading(false)
    }
  }

  const onSubmit = async (data: InviteForm) => {
    try {
      setIsLoading(true)

      const parsedStartingPoints =
        canAssignPoints && data.startingPoints != null && data.startingPoints !== ''
          ? Math.max(0, Math.min(9999, Math.round(Number(data.startingPoints))))
          : undefined

      await invitationService.createInvitation({
        roomId: isWorld ? undefined : roomId,
        kind,
        recipientEmail: data.recipientEmail,
        role: isCompanyRoom ? data.role : undefined,
        startingPoints: parsedStartingPoints,
      })
      
      toast.success(t('invitations.sentSuccess'))
      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send invitation')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-4 glass-strong rounded-2xl p-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-display font-bold text-foreground">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all" aria-label={t('common.close')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-2">
              Player Email
            </label>
            <input
              {...register('recipientEmail', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              type="email"
              className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder="Enter player email"
            />
            {errors.recipientEmail && (
              <p className="mt-2 text-sm text-destructive">{errors.recipientEmail.message}</p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">{t('invitations.inviteEmailHint')}</p>
          </div>

          {isCompanyRoom && (
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-2">
                Player Role {isCompanyRoom && <span className="text-destructive">*</span>}
              </label>
              <select
                {...register('role', { 
                  required: isCompanyRoom ? 'Role is required for company rooms' : false 
                })}
                className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              >
                {corporateRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-2 text-sm text-destructive">{errors.role.message}</p>
              )}
            </div>
          )}

          {canAssignPoints && (
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-2">
                {t('roomPage.startingPoints')}
              </label>
              <input
                {...register('startingPoints', {
                  min: { value: 0, message: t('roomPage.startingPointsRange') },
                  max: { value: 9999, message: t('roomPage.startingPointsRange') },
                })}
                type="number"
                min={0}
                max={9999}
                step={1}
                className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                placeholder="0"
              />
              <p className="mt-2 text-xs text-muted-foreground">{t('roomPage.startingPointsHint')}</p>
              {errors.startingPoints && (
                <p className="mt-2 text-sm text-destructive">{errors.startingPoints.message}</p>
              )}
            </div>
          )}

          {!isWorld && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase">{t('agent.draftInvite')}</p>
            <textarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder={t('agent.inviteDraftPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-background border border-muted/20 text-sm text-foreground"
            />
            <button
              type="button"
              onClick={() => void handleDraftInvite()}
              disabled={draftLoading}
              className="w-full px-3 py-2 rounded-lg border border-primary/40 text-sm font-medium hover:bg-primary/10 disabled:opacity-50"
            >
              {draftLoading ? t('agent.loading') : `✨ ${t('agent.draftInviteButton')}`}
            </button>
            {inviteDraft && (
              <div className="text-sm text-foreground whitespace-pre-wrap rounded-lg bg-background/80 p-3 border border-muted/20">
                <p className="text-xs text-muted-foreground mb-1">{inviteDraft.subject}</p>
                {inviteDraft.body}
              </div>
            )}
          </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-muted/20 border border-muted/20 text-foreground hover:bg-muted/30 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InvitePlayerModal
